import { Suspense, useState } from 'react';
import { Alert } from '@startuptools/ui/Alert';
import { Box } from '@startuptools/ui/Box';
import { Stack } from '@startuptools/ui/Stack';
import { Switch } from '@startuptools/ui/Switch';
import { Typography } from '@startuptools/ui/Typography';
import { Link } from '@startuptools/ui/Link';
import { TermsLink } from '@startuptools/ui/links/Terms';
import CheckIcon from '@mui/icons-material/Check';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useStripeProductsSuspenseQuery } from '../../graphql/react-operations';
import { stripeProducts } from '../../../environments/environment';
import { SubscriptionCard, SubscriptionCardSkeleton } from './SubscriptionCard';
import { Trans } from '@lingui/react';
import { type GqlCompanySubscriptionQuery } from '../../company/billing/Subscriptions.graphql';
import { getCurrentPrice } from '../../company/billing/billing.utils';
import { FREE_SHAREHOLDERS_LIMIT } from '@startuptools/business-ui/UpgradeBanner';

export const SubPicker = ({
  onSubSelected,
  disableStartup = false,
  currentSubscription,
}: {
  disableStartup?: boolean;
  currentSubscription?: GqlCompanySubscriptionQuery['stripeCompanySubscription'];
  onSubSelected: (priceId: string, prodId: string) => Promise<boolean>;
}) => {
  const { data } = useStripeProductsSuspenseQuery({});
  const [yearly, setYearly] = useState(
    currentSubscription ? getCurrentPrice(currentSubscription)?.recurring?.interval === 'year' : true,
  );
  const [loadingSubscriptionSession, setLoadingSubscriptionSession] = useState(false);

  const prods = data.stripeProducts;
  const startup = prods?.find(p => p.id === stripeProducts.startup);
  const grow = prods?.find(p => p.id === stripeProducts.grow);
  const pro = prods?.find(p => p.id === stripeProducts.pro);
  const butler = prods?.find(p => p.id === stripeProducts.proButler);

  if (!startup || !grow || !pro || !butler) {
    return;
  }

  const handleOnSubSelected = async (priceId: string, prodId: string) => {
    const ret = onSubSelected(priceId, prodId);
    setLoadingSubscriptionSession(true);
    const setLoading = await ret.catch(() => false);
    setLoadingSubscriptionSession(setLoading);
  };

  return (
    <Stack direction="column" alignItems="center" gap={2}>
      <Switch
        checked={yearly}
        onChange={() => setYearly(!yearly)}
        endDecorator={<Trans id="Pay per year and save 17%" />}
      />
      <Alert variant="soft" startDecorator={<InfoOutlinedIcon />}>
        <Stack direction="column" gap={1}>
          <Box>
            <Typography level="body-sm">
              <Trans id="All contracts run 12 months at a time, all contracts are renewed automatically for another 12 month period. All pricing is exclusive of VAT (moms)." />
            </Typography>
          </Box>
          <Box>
            <Typography level="body-sm">
              <Trans
                id="Our <termsLink>terms of service</termsLink> apply. You can read about our additional products, services and prices <pricesLink>here</pricesLink>."
                components={{
                  termsLink: <TermsLink />,
                  pricesLink: <Link href="https://bolago.com/se/priser" target="_blank" />,
                }}
              />
            </Typography>
          </Box>
        </Stack>
      </Alert>

      {!currentSubscription && (
        <Alert variant="outlined">
          <Typography fontWeight={700}>
            <Trans
              id="Current plan: <uppercase>Limited Share Register <primaryColor>Free</primaryColor></uppercase>"
              components={{
                uppercase: <Typography sx={{ textTransform: 'uppercase' }} />,
                primaryColor: <Typography color="primary" />,
              }}
            />
          </Typography>
          <Stack direction="row" gap={1}>
            <Stack direction="row" fontSize="small" alignItems="center">
              <CheckIcon color="success" fontSize="small" />
              <Trans
                id="Up to {numberOfShareholders} shareholders"
                values={{ numberOfShareholders: FREE_SHAREHOLDERS_LIMIT }}
              />
            </Stack>
            <Stack direction="row" fontSize="small" alignItems="center">
              <CheckIcon color="success" fontSize="small" />
              <Trans id="Synchronized with Bolagsverket" />
            </Stack>
          </Stack>
        </Alert>
      )}

      <Stack direction="row" gap={4} sx={{ paddingX: 4 }}>
        <Suspense fallback={<SubscriptionCardSkeleton />}>
          <SubscriptionCard
            id={startup.id}
            yearly={yearly}
            onSubSelected={handleOnSubSelected}
            disabled={disableStartup || loadingSubscriptionSession}
            currentSubscription={currentSubscription}
          />
        </Suspense>
        <Suspense fallback={<SubscriptionCardSkeleton />}>
          <SubscriptionCard
            id={grow.id}
            yearly={yearly}
            onSubSelected={handleOnSubSelected}
            disabled={loadingSubscriptionSession}
            currentSubscription={currentSubscription}
          />
        </Suspense>
        <Suspense fallback={<SubscriptionCardSkeleton />}>
          <SubscriptionCard
            id={pro.id}
            yearly={yearly}
            onSubSelected={handleOnSubSelected}
            disabled={loadingSubscriptionSession}
            currentSubscription={currentSubscription}
          />
        </Suspense>
      </Stack>
    </Stack>
  );
};
